import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { ChevronRight, RefreshCw } from "lucide-react";
import { Alert } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import SiteDetailItem from "./SiteDetailItem";
import SiteKeyActivityTable from "./SiteKeyActivityTable";
import SiteKeyAuditTable from "./SiteKeyAuditTable";
import {
  type PhysicalKeyExternalSync,
  requestErrorMessage,
  useRetrySisterSync,
  useSiteKey,
} from "./siteKeysApi";
import { formatKeyDate, formatUtcTimestamp } from "./siteKeyLabels";
import { CredentialValue, ExternalSyncStatusBadge, GuestLink } from "./SiteKeyParts";
import { useIsSuperUser } from "shared/api/useIsSuperUser";

const DETAIL_TABS = [
  { to: "activity", label: "Activity" },
  { to: "audit", label: "Audit" },
] as const;

/** Where the "Keys" breadcrumb returns to: the list (or audit log) the fob was opened from. */
function useKeysHref(siteId: string): string {
  const state = useLocation().state as { from?: unknown } | null;
  const from = typeof state?.from === "string" ? state.from : undefined;
  const keysPath = `/admin/sites/${siteId}/keys`;
  return from?.startsWith(keysPath) ? from : keysPath;
}

/** Opens the Activity sub-tab, keeping the breadcrumb's way back to the list. */
function RedirectToTab({ siteId, credential }: { siteId: string; credential: string }) {
  const { state } = useLocation();
  return (
    <Navigate
      replace
      state={state}
      to={`/admin/sites/${siteId}/keys/${encodeURIComponent(credential)}/${DETAIL_TABS[0].to}`}
    />
  );
}

function DetailTabs({ siteId, credential }: { siteId: string; credential: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const fobPath = `/admin/sites/${siteId}/keys/${encodeURIComponent(credential)}`;
  const active =
    DETAIL_TABS.find(
      ({ to }) =>
        location.pathname === `${fobPath}/${to}` ||
        location.pathname.startsWith(`${fobPath}/${to}/`),
    )?.to ?? DETAIL_TABS[0].to;

  return (
    <Tabs
      value={active}
      // Keep the breadcrumb's way back to the list.
      onValueChange={(value) => navigate(`${fobPath}/${value}`, { state: location.state })}
    >
      <TabsList aria-label="Fob views" className="justify-start w-full">
        {DETAIL_TABS.map(({ to, label }) => (
          <TabsTrigger
            key={to}
            value={to}
            className="shrink-0 data-[state=active]:bg-slate-200!"
          >
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

function ExternalSyncRow({ sync }: { sync: PhysicalKeyExternalSync }) {
  const fmt = (value: string | null) => formatUtcTimestamp(value, "Never");

  return (
    <article className="p-4" aria-label={sync.destinationSiteName || sync.destinationSiteId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">
            <Link
              className="hover:text-blue-700 hover:underline"
              to={`/admin/sites/${encodeURIComponent(sync.destinationSiteId)}`}
            >
              {sync.destinationSiteName || sync.destinationSiteId}
            </Link>
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">{sync.externalSystemType}</p>
        </div>
        <ExternalSyncStatusBadge sync={sync} />
      </div>
      {sync.statusReason && (
        <p
          className={
            sync.status === "Failed"
              ? "mt-2 text-sm text-rose-700"
              : "mt-2 text-sm text-slate-600"
          }
        >
          {sync.statusReason}
        </p>
      )}
      <dl className="grid grid-cols-2 mt-4 text-sm gap-x-4 gap-y-3">
        <div>
          <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
            Last synced
          </dt>
          <dd className="mt-1 font-medium text-slate-800">{fmt(sync.lastSyncedTimestamp)}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
            Last attempt
          </dt>
          <dd className="mt-1 font-medium text-slate-800">{fmt(sync.lastAttemptTimestamp)}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
            External credential
          </dt>
          <dd className="mt-1 font-medium break-all text-slate-800">
            {sync.externalCredentialId ?? "—"}
            {sync.externalCredentialFormat && (
              <span className="ml-1 text-xs font-normal text-slate-500">
                (format {sync.externalCredentialFormat})
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
            Holder
          </dt>
          <dd className="mt-1 font-medium break-all text-slate-800">
            {sync.externalUserId ?? "No one"}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
            Created by Livly
          </dt>
          <dd className="mt-1 font-medium text-slate-800">
            {sync.createdByLivly ? "Yes" : "No"}
          </dd>
        </div>
      </dl>
    </article>
  );
}

function ExternalSyncSection({
  siteId,
  credential,
  syncs,
  canRetry,
}: {
  siteId: string;
  credential: string;
  syncs: PhysicalKeyExternalSync[];
  canRetry: boolean;
}) {
  const retry = useRetrySisterSync(siteId, credential);

  return (
    <section
      aria-labelledby="fob-external-sync-heading"
      className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200"
    >
      <header className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-200">
        <div>
          <h2 id="fob-external-sync-heading" className="text-base font-semibold text-slate-900">
            External sync
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">This fob on sister sites</p>
        </div>
        {canRetry && (
          <Button
            disabled={retry.isPending}
            onClick={() => retry.mutate()}
            size="sm"
            type="button"
            variant="outline"
          >
            <RefreshCw className={retry.isPending ? "animate-spin" : undefined} />
            {retry.isPending ? "Retrying..." : "Retry sync"}
          </Button>
        )}
      </header>
      {retry.isError && (
        <Alert className="m-4" variant="destructive">
          Retry failed: {requestErrorMessage(retry.error, "the sync didn't complete.")}
        </Alert>
      )}
      {retry.isSuccess && (
        <p className="px-4 pt-3 text-xs text-emerald-700" role="status">
          Sync complete.
        </p>
      )}
      {syncs.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-500">
          Not synced to a sister site yet.
        </p>
      ) : (
        <div className="divide-y divide-slate-100">
          {syncs.map((sync) => (
            <ExternalSyncRow key={sync.destinationSiteId} sync={sync} />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * The fob detail inside the site page's Keys tab (`keys/:credential/*`): a summary, the fob's
 * state on sister sites with a Retry, and Activity / Audit sub-tabs.
 */
export default function SiteKeyDetail({
  siteId,
  externalSyncEnabled,
}: {
  siteId: string;
  /** A Schlage site sharing physical keys with sister sites. */
  externalSyncEnabled: boolean;
}) {
  const credential = useParams<{ credential: string }>().credential ?? "";
  const keysHref = useKeysHref(siteId);
  const { data: key, isLoading, isError, error } = useSiteKey(siteId, credential);
  const fobLabel = key?.badgeId || credential;
  const externalSyncs = key?.externalSyncs ?? [];
  // Shown on sharing sites, and wherever a fob still has sync records (e.g. sharing turned off).
  const showExternalSync = externalSyncEnabled || externalSyncs.length > 0;
  // Retrying the sister-site sync is a super-user action (the API refuses it for others).
  const canRetry = useIsSuperUser() && externalSyncEnabled;
  const schlageBadgeId = key?.schlageBadgeId;

  return (
    <div className="space-y-6">
      <nav aria-label="Breadcrumb">
        <ol className="flex items-center gap-1.5 text-sm">
          <li>
            <Link
              className="font-semibold text-slate-600 hover:text-blue-700 hover:underline"
              to={keysHref}
            >
              Keys
            </Link>
          </li>
          <li aria-hidden="true">
            <ChevronRight className="size-4 text-slate-400" />
          </li>
          <li aria-current="page" className="font-semibold text-slate-900">
            Fob {fobLabel}
          </li>
        </ol>
      </nav>

      {isError ? (
        <Alert variant="destructive">
          Error loading this fob: {requestErrorMessage(error, "it may not exist on this site.")}
        </Alert>
      ) : isLoading || !key ? (
        <div className="flex items-center justify-center text-sm bg-white border rounded-lg shadow-sm min-h-40 border-slate-200 text-slate-500">
          Loading fob...
        </div>
      ) : (
        <div
          className={
            showExternalSync ? "grid items-start gap-6 lg:grid-cols-2" : "grid gap-6"
          }
        >
          <section
            aria-labelledby="fob-summary-heading"
            className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200"
          >
            <header className="px-4 py-3 border-b border-slate-200">
              <h2 id="fob-summary-heading" className="text-base font-semibold text-slate-900">
                Fob {fobLabel}
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">Physical key details</p>
            </header>
            <dl className="divide-y divide-slate-100">
              <SiteDetailItem label="Fob #">{key.badgeId || "—"}</SiteDetailItem>
              <SiteDetailItem label="Credential">
                <CredentialValue credential={key.credential} />
              </SiteDetailItem>
              <SiteDetailItem label="Assigned to">
                {key.guestId ? (
                  <GuestLink guestId={key.guestId} />
                ) : key.physicalKeyGroupId ? (
                  <span title={key.physicalKeyGroupId}>Key group</span>
                ) : (
                  "—"
                )}
              </SiteDetailItem>
              <SiteDetailItem label="Valid from">{formatKeyDate(key.validFrom)}</SiteDetailItem>
              <SiteDetailItem label="Valid to">{formatKeyDate(key.validTo)}</SiteDetailItem>
              <SiteDetailItem label="Last encoded">
                {formatKeyDate(key.encodedTimestamp)}
              </SiteDetailItem>
              <SiteDetailItem label="Format">
                {key.format ? <Badge variant="outline">{key.format}</Badge> : "—"}
              </SiteDetailItem>
              {schlageBadgeId && schlageBadgeId !== key.badgeId && (
                <SiteDetailItem label="Schlage badge ID">{schlageBadgeId}</SiteDetailItem>
              )}
            </dl>
          </section>
          {showExternalSync && (
            <ExternalSyncSection
              canRetry={canRetry}
              credential={key.credential}
              siteId={siteId}
              syncs={externalSyncs}
            />
          )}
        </div>
      )}

      <div className="space-y-4">
        <DetailTabs siteId={siteId} credential={credential} />
        <Routes>
          <Route
            index
            element={<RedirectToTab siteId={siteId} credential={credential} />}
          />
          <Route
            path="activity"
            element={<SiteKeyActivityTable siteId={siteId} credential={credential} />}
          />
          <Route
            path="audit"
            element={<SiteKeyAuditTable siteId={siteId} credential={credential} />}
          />
          <Route
            path="*"
            element={<RedirectToTab siteId={siteId} credential={credential} />}
          />
        </Routes>
      </div>
    </div>
  );
}
